/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faSync,
  faPlus,
  faBell,
  faEye,
  faSyncAlt,
  faTrash,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import "../../components/skeletonRow.css";
import AddProductModal from "./AddProductModal";
import RenewProductModal from "./RenewProductModal";

const ProductSubscriptionList = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [allProductsData, setAllProductsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingAlerts, setCheckingAlerts] = useState(false);

  // Form inputs for inline header filter row
  const [formInputs, setFormInputs] = useState({
    search: "",
    type: "",
    status: "",
  });

  // Active filter state
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    status: "",
    orderbycol: "expiry_date",
    orderby: "asc",
    page: 1,
    limit: 50,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    showingRecords: "Showing 0 to 0 of 0 entries",
  });

  // Double scrollbar refs
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [renewingProduct, setRenewingProduct] = useState(null);

  // Fetch product list
  const fetchProducts = useCallback(
    async (targetPage = filters.page) => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        const params = new URLSearchParams();
        if (filters.search?.trim()) params.append("search", filters.search.trim());
        if (filters.type) params.append("type", filters.type);
        if (filters.status) params.append("status", filters.status);
        params.append("sortBy", filters.orderbycol);
        params.append("sortOrder", filters.orderby.toUpperCase());

        const response = await axios.get(
          `${API_URL}settings/product-subscriptions?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        if (response.data?.success && response.data?.data) {
          const list = response.data.data.products || [];
          setAllProductsData(list);

          const totalRecords = list.length;
          const totalPages = Math.ceil(totalRecords / filters.limit) || 1;
          const pageToUse = Math.max(1, Math.min(targetPage, totalPages));

          const startRecord = totalRecords > 0 ? (pageToUse - 1) * filters.limit + 1 : 0;
          const endRecord = Math.min(pageToUse * filters.limit, totalRecords);

          const pagedList = list.slice(
            (pageToUse - 1) * filters.limit,
            pageToUse * filters.limit
          );

          setProducts(pagedList);
          setPagination({
            currentPage: pageToUse,
            totalPages: totalPages,
            totalRecords: totalRecords,
            showingRecords: `Showing ${startRecord} to ${endRecord} of ${totalRecords} entries`,
          });
        }
      } catch (err) {
        console.error("Error fetching product subscriptions:", err);
        showToast.error("Failed to load product subscriptions.");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchProducts(filters.page);
  }, [fetchProducts, filters.page]);

  // Sync scrollbars
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };

    updateScrollbar();
    window.addEventListener("resize", updateScrollbar);
    return () => window.removeEventListener("resize", updateScrollbar);
  }, [products]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  // Form input changes for text filter
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Apply filters on Enter
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const applyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      search: formInputs.search,
      type: formInputs.type,
      status: formInputs.status,
      page: 1,
    }));
  };

  // Dropdown filter changes apply immediately
  const handleDropdownFilterChange = (field, value) => {
    setFormInputs((prev) => ({ ...prev, [field]: value }));
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  // Sort handler
  const handleSort = (column) => {
    setFilters((prev) => ({
      ...prev,
      orderbycol: column,
      orderby: prev.orderbycol === column && prev.orderby === "asc" ? "desc" : "asc",
      page: 1,
    }));
  };

  const getSortIcon = (column) => {
    if (filters.orderbycol !== column) return faSort;
    return filters.orderby === "asc" ? faSortUp : faSortDown;
  };

  // Pagination handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFormInputs({
      search: "",
      type: "",
      status: "",
    });
    setFilters({
      search: "",
      type: "",
      status: "",
      orderbycol: "expiry_date",
      orderby: "asc",
      page: 1,
      limit: 50,
    });
  };

  const hasAnyFilter = () => {
    return (
      Boolean(filters.search) ||
      Boolean(filters.type) ||
      Boolean(filters.status) ||
      filters.orderbycol !== "expiry_date" ||
      filters.orderby !== "asc"
    );
  };

  // Alert check trigger
  const handleTriggerAlerts = async () => {
    try {
      setCheckingAlerts(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_URL}settings/product-subscriptions/check-alerts`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        const info = response.data.data;
        showToast.success(
          `Alert check finished! Generated ${info.alerts_generated} alert(s).`
        );
        fetchProducts(1);
      }
    } catch (err) {
      console.error("Error checking alerts:", err);
      showToast.error("Failed to run alert check.");
    } finally {
      setCheckingAlerts(false);
    }
  };

  // Delete product
  const handleDelete = async (e, prod) => {
    e.stopPropagation();

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${prod.product_name}" and all renewal history?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (!confirm.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(
        `${API_URL}settings/product-subscriptions/${prod.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      showToast.success("Product deleted successfully");
      fetchProducts(filters.page);
    } catch (err) {
      console.error("Error deleting product:", err);
      showToast.error("Failed to delete product.");
    }
  };

  // Format date DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00") return "—";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "—";
      return moment(date).format("DD-MM-YYYY");
    } catch {
      return "—";
    }
  };

  // Status badge matching CRM report styling
  const renderStatusBadge = (prod) => {
    if (prod.is_expiring_soon && prod.status === "active") {
      return (
        <span
          className="badge badge-warning font-weight-normal"
          style={{ backgroundColor: "#f59c1a", color: "#fff" }}
        >
          Expiring Soon
        </span>
      );
    }
    if (prod.is_low_balance && prod.type === "credits") {
      return (
        <span
          className="badge badge-warning font-weight-normal"
          style={{ backgroundColor: "#f59c1a", color: "#fff" }}
        >
          Low Balance
        </span>
      );
    }

    switch (prod.status) {
      case "active":
        return (
          <span
            className="badge badge-success font-weight-normal"
            style={{ backgroundColor: "#32a932", color: "#fff" }}
          >
            Active
          </span>
        );
      case "low_balance":
        return (
          <span
            className="badge badge-warning font-weight-normal"
            style={{ backgroundColor: "#f59c1a", color: "#fff" }}
          >
            Low Balance
          </span>
        );
      case "expired":
        return (
          <span
            className="badge badge-danger font-weight-normal"
            style={{ backgroundColor: "#ff5b57", color: "#fff" }}
          >
            Expired
          </span>
        );
      case "cancelled":
        return (
          <span
            className="badge font-weight-normal"
            style={{ backgroundColor: "#6c757d", color: "#fff" }}
          >
            Cancelled
          </span>
        );
      default:
        return (
          <span
            className="badge font-weight-normal"
            style={{ backgroundColor: "#6c757d", color: "#fff" }}
          >
            {prod.status}
          </span>
        );
    }
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div>
          {/* Standard Report Top Header and Controls */}
          <div className="panel-heading d-flex justify-content-between">
            <div className="col-md-4">
              <div style={{ float: "left", position: "relative", marginLeft: "5px" }}>
                {hasAnyFilter() && (
                  <button
                    className="btn btn-default btn-xs ml-1"
                    style={{ marginLeft: "6px" }}
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
            <div>
              <span style={{ fontSize: "0.8rem" }} className="me-3">
                {loading ? (
                  <span
                    className="skeleton-text"
                    style={{ width: "140px", display: "inline-block" }}
                  ></span>
                ) : (
                  pagination.showingRecords
                )}
              </span>

              <button
                className="btn btn-default btn-sm mx-1"
                onClick={() => fetchProducts(1)}
                title="Refresh"
              >
                <FontAwesomeIcon icon={faSync} />
              </button>

              <button
                className="btn btn-default btn-sm mx-1"
                onClick={handleTriggerAlerts}
                disabled={checkingAlerts}
                title="Scan and send daily expiry/balance alerts"
              >
                <FontAwesomeIcon
                  icon={faBell}
                  className={checkingAlerts ? "fa-spin text-primary me-1" : "text-warning me-1"}
                />
                {checkingAlerts ? "Checking..." : "Check Alerts"}
              </button>

              <button
                className="btn btn-primary btn-sm mx-1"
                onClick={() => setIsAddModalOpen(true)}
                title="Add New Product / Service"
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Product
              </button>

              <button
                className={`btn btn-default btn-sm ${
                  pagination.currentPage <= 1 ? "d-none" : ""
                }`}
                title="Previous"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className={`btn btn-default btn-sm ${
                  pagination.currentPage >= pagination.totalPages ? "d-none" : ""
                }`}
                title="Next"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>

          {/* Standard Report Main Panel */}
          <div className="panel panel-default with-rounded-corner with-shadow">
            <div className="large-table-container-3">
              {/* Top Double Scrollbar */}
              <div
                ref={topScrollbarRef}
                onScroll={handleTopScroll}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "16px",
                  marginBottom: "2px",
                  background: "#f8f9fa",
                }}
              >
                <div
                  ref={topScrollbarContentRef}
                  style={{ height: "1px" }}
                ></div>
              </div>

              {/* Table Container */}
              <div
                ref={tableContainerRef}
                onScroll={syncScroll}
                style={{
                  overflowY: "auto",
                  height: "calc(112vh - 250px)",
                  overflowX: "auto",
                  position: "relative",
                }}
              >
                <table className="table table-condensed table-hover bg-white sticky-table">
                  <thead>
                    {/* Header Row */}
                    <tr style={{ cursor: "pointer" }}>
                      <th style={{ width: "40px", textAlign: "center" }}>#</th>

                      <th onClick={() => handleSort("product_name")}>
                        Product / Service{" "}
                        <FontAwesomeIcon icon={getSortIcon("product_name")} />
                      </th>

                      <th style={{ width: "130px" }} onClick={() => handleSort("type")}>
                        Type <FontAwesomeIcon icon={getSortIcon("type")} />
                      </th>

                      <th onClick={() => handleSort("expiry_date")}>
                        Current Expiry / Remaining Credits{" "}
                        <FontAwesomeIcon icon={getSortIcon("expiry_date")} />
                      </th>

                      <th style={{ width: "130px" }} onClick={() => handleSort("status")}>
                        Status <FontAwesomeIcon icon={getSortIcon("status")} />
                      </th>

                      <th
                        style={{ width: "140px" }}
                        onClick={() => handleSort("amount_paid")}
                      >
                        Amount Paid{" "}
                        <FontAwesomeIcon icon={getSortIcon("amount_paid")} />
                      </th>

                      <th style={{ width: "90px" }} className="ctc">
                        Cycle
                      </th>

                      <th style={{ width: "120px" }} className="ctc">
                        Action
                      </th>
                    </tr>

                    {/* Filter Row */}
                    <tr>
                      <th></th>

                      <th>
                        <div className="d-flex align-items-center gap-1">
                          <input
                            type="text"
                            name="search"
                            placeholder="Search name/company..."
                            value={formInputs.search}
                            onChange={handleFormInputChange}
                            onKeyDown={handleKeyDown}
                            style={{ width: "160px", padding: "5px" }}
                          />
                          <button
                            type="button"
                            className="btn btn-default btn-xs"
                            onClick={applyFilters}
                            title="Search"
                          >
                            <FontAwesomeIcon icon={faSearch} />
                          </button>
                        </div>
                      </th>

                      <th>
                        <select
                          name="type"
                          value={formInputs.type}
                          onChange={(e) =>
                            handleDropdownFilterChange("type", e.target.value)
                          }
                          style={{ width: "115px", padding: "5px" }}
                        >
                          <option value="">All Types</option>
                          <option value="subscription">Subscription</option>
                          <option value="credits">Credits</option>
                        </select>
                      </th>

                      <th></th>

                      <th>
                        <select
                          name="status"
                          value={formInputs.status}
                          onChange={(e) =>
                            handleDropdownFilterChange("status", e.target.value)
                          }
                          style={{ width: "120px", padding: "5px" }}
                        >
                          <option value="">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="expiring_soon">Expiring Soon</option>
                          <option value="low_balance">Low Balance</option>
                          <option value="expired">Expired</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </th>

                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      Array.from({ length: 10 }).map((_, idx) => (
                        <tr key={`skeleton-${idx}`} className="skeleton-row">
                          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                        </tr>
                      ))
                    ) : products.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-muted">
                          No product subscriptions found.
                        </td>
                      </tr>
                    ) : (
                      products.map((prod, index) => {
                        const companyName =
                          prod.contact_details?.company_info?.company_name;
                        const latest = prod.latest_renewal;
                        const rowIndex =
                          (pagination.currentPage - 1) * filters.limit + index + 1;

                        return (
                          <tr
                            key={prod.id}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              navigate(`/research/ProductSubscriptions/${prod.id}`)
                            }
                          >
                            <td className="ctc">{rowIndex}</td>

                            <td>
                              <strong style={{ color: "#242a30" }}>
                                {prod.product_name}
                              </strong>
                              {companyName && (
                                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                                  {companyName}
                                </div>
                              )}
                            </td>

                            <td>
                              {prod.type === "subscription" ? (
                                <span>Subscription</span>
                              ) : (
                                <span>Prepaid Credits</span>
                              )}
                            </td>

                            <td>
                              {prod.type === "subscription" ? (
                                <div>
                                  <strong>
                                    {latest?.end_date
                                      ? formatDate(latest.end_date)
                                      : "—"}
                                  </strong>
                                  {prod.days_until_expiry !== null && (
                                    <span
                                      className="ms-2"
                                      style={{
                                        fontSize: "11px",
                                        marginLeft: "6px",
                                        color:
                                          prod.days_until_expiry < 0
                                            ? "#ff5b57"
                                            : prod.is_expiring_soon
                                            ? "#f59c1a"
                                            : "#32a932",
                                      }}
                                    >
                                      {prod.days_until_expiry < 0
                                        ? `(Expired ${Math.abs(
                                            prod.days_until_expiry
                                          )}d ago)`
                                        : `(${prod.days_until_expiry}d left)`}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <strong>
                                    {Number(
                                      latest?.credits_remaining || 0
                                    ).toLocaleString("en-IN")}{" "}
                                    credits
                                  </strong>
                                  {latest?.low_balance_alert_at !== null && (
                                    <span
                                      className="text-muted ms-1"
                                      style={{
                                        fontSize: "11px",
                                        marginLeft: "6px",
                                      }}
                                    >
                                      (Alert &le;{" "}
                                      {Number(
                                        latest.low_balance_alert_at
                                      ).toLocaleString("en-IN")}
                                      )
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>

                            <td>{renderStatusBadge(prod)}</td>

                            <td className="ctc">
                              {latest?.amount_paid !== undefined
                                ? `₹${Number(latest.amount_paid).toLocaleString(
                                    "en-IN"
                                  )}`
                                : "—"}
                            </td>

                            <td className="ctc">
                              {latest ? `Cycle #${latest.renewal_number}` : "—"}
                            </td>

                            <td
                              className="ctc"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="btn btn-default btn-xs mx-1"
                                title="View Details & History"
                                onClick={() =>
                                  navigate(
                                    `/research/ProductSubscriptions/${prod.id}`
                                  )
                                }
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>

                              <button
                                type="button"
                                className="btn btn-default btn-xs mx-1"
                                title="Renew / Recharge"
                                onClick={() => setRenewingProduct(prod)}
                              >
                                <FontAwesomeIcon
                                  icon={faSyncAlt}
                                  className="text-primary"
                                />
                              </button>

                              <button
                                type="button"
                                className="btn btn-default btn-xs mx-1"
                                title="Delete"
                                onClick={(e) => handleDelete(e, prod)}
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="text-danger"
                                />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddProductModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => fetchProducts(1)}
      />

      <RenewProductModal
        isOpen={Boolean(renewingProduct)}
        product={renewingProduct}
        onClose={() => setRenewingProduct(null)}
        onSuccess={() => fetchProducts(filters.page)}
      />

      {/* Embedded Scoped Style matching CRM Reports */}
      <style>{`
        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .with-rounded-corner {
          border-radius: 4px;
        }

        .with-shadow {
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .table-condensed th,
        .table-condensed td {
          padding: 8px 5px;
          font-size: 12px;
        }

        .btn-default {
          background: #f8f9fa;
          border: 1px solid #ddd;
        }

        .btn-xs {
          padding: 1px 5px;
          font-size: 12px;
          line-height: 1.5;
        }

        th {
          white-space: nowrap;
          background: #f8f9fa;
        }

        .table-hover tbody tr:hover {
          background-color: #f5f5f5;
        }

        .panel-heading {
          padding: 10px 15px;
          border-bottom: 1px solid #eee;
          background: #f8f9fa;
        }

        .large-table-container-3 {
          position: relative;
          overflow: hidden;
        }

        .sticky-table {
          margin-bottom: 0;
        }

        .ctc {
          text-align: center;
        }

        .badge {
          font-size: 11px;
          padding: 3px 6px;
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
};

export default ProductSubscriptionList;
